import React, { useState } from "react";
import { Formik, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputLabel,
  Tooltip,
} from "@mui/material";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import MUITable from "../../../../../common/components/mui-table/mui-table";
import addCirculIcon from "../../../../../assets/icons/addREC.svg";
import {
  SelectInputComponent,
  TextInputComponent,
} from "../../../../../common/components";
import DatePickerComponent from "../../../../../common/components/date-picker/date-picker-compoenent";
import CompanyDashboardAddExistingObjectivesPopup from "../../../company-dashboard-add-existing-objectives-popup/company-dashboard-add-existing-objectives-popup";
import deleteRedIcon from "../../../../../assets/icons/linkBreak.svg";
import WarningConfirmationPopup from "../../../../../common/components/warning-confirmation-popup/warning-confirmation-popup";
import CloseIcon from "../../../../../assets/icons/closeIcon.svg";
import "./company-dashboard-add-key-results-popup.scss";
import { StatusOption } from "../company-dashboard-add-object-popup/company-dashboard-add-object-popup";
import dayjs from "dayjs";
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";
import { debounce } from "lodash";
import { getKeyResultTitle } from "../../../../../api/title-search";

const CompanyDashboardAddKeyResultsPopup: any = (props: any) => {
  const {
    isOpenAddPopup,
    onCancel,
    employeesList,
    onSave,
    statusListObjectives,
    setLoading,
    objectivesData,
  } = props;
  const [isOpenAddObjectivePopup, setIsOpenAddObjectivePopup] = useState(false);
  const [isDeleteWarningOpen, setIsDeleteWarningOpen] = useState(false);
  const [selectedDeleteObjective, setSelectedDeleteObjective] =
    useState<any>(null);
  const headers = [
    { key: "title", label: "Title" },
    { key: "weightage", label: "Weightage" },
    { key: "actions", label: "" },
  ];
  const [titleSuggestions, setTitleSuggestions] = useState<any[]>([]);
  const [loadingTitles, setLoadingTitles] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [autocompleteOpen, setAutocompleteOpen] = useState(false);

  // Clear search state when popup closes
  React.useEffect(() => {
    if (!isOpenAddPopup) {
      setTitleSuggestions([]);
      setHasSearched(false);
      setAutocompleteOpen(false);
      setLoadingTitles(false);
    }
  }, [isOpenAddPopup]);

  // Debounced function to search for initiative titles
  const searchKeyResultTitles = React.useMemo(
    () =>
      debounce(async (searchValue: string) => {
        if (!searchValue || searchValue.trim().length < 2) {
          setTitleSuggestions([]);
          setHasSearched(false);
          return;
        }

        setLoadingTitles(true);
        setHasSearched(true);
        try {
          const response = await getKeyResultTitle({ title: searchValue });
          const keyresults = response?.data?.data || [];
          setTitleSuggestions(keyresults);
        } catch (error: any) {
          setTitleSuggestions([]);
          console.error("Error fetching key result titles:", error);
        } finally {
          setLoadingTitles(false);
        }
      }, 500),
    [],
  );
  const handleCloseAddObjectivePopup = () => {
    setIsOpenAddObjectivePopup(false);
  };
  const handleOpenAddObjectivePopup = () => {
    setIsOpenAddObjectivePopup(true);
  };

  const getInitialValues = () => {
    return {
      title: "",
      description: "",
      owner: [],
      ownerId: [],
      startDate: null,
      endDate: null,
      weightage: "",
      comments: "",
      selectedObjectiveData: [
        {
          objectiveId: objectivesData?.pk || objectivesData?.id,
          objectiveName: objectivesData?.objectiveName,
          weightage: objectivesData?.weightage,
        },
      ],
    };
  };

  const onSubmitHandler = (values: any, { resetForm }: any) => {
    const newKeyResult = {
      ...values,
      initiatives: [],
      isSelected: true,
      owners: values.ownerId,
      selectedObjectiveData: values.selectedObjectiveData.map((obj: any) => ({
        objectiveId: obj.objectiveId,
        weightage: obj.weightage,
      })),
    };
    onSave(newKeyResult);
    onCancel();
    resetForm();
  };

  const getStatusFormattedData = (
    statusName: any,
    setFieldValue: any,
    data: any,
    item: any,
  ) => {
    return (
      <div className="form-group form-group-status form-text-input-component">
        <Autocomplete<StatusOption>
          value={statusName}
          onChange={(event, newValue) => {
            setFieldValue(
              "selectedObjectiveData",
              data.map((obj: any) =>
                obj.objectiveId === item.objectiveId
                  ? { ...obj, status: newValue }
                  : obj,
              ),
            );
          }}
          options={statusListObjectives || []}
          getOptionLabel={(option) => option.statusName}
          isOptionEqualToValue={(option, value) =>
            option.statusId === value.statusId
          }
          renderInput={(params) => <TextField {...params} label="" />}
          sx={{
            "& .MuiAutocomplete-option": {
              fontSize: "13px",
              fontFamily: "Manrope, sans-serif",
            },
          }}
          slotProps={{
            popper: {
              sx: {
                "& .MuiAutocomplete-option": {
                  fontSize: "13px",
                  fontFamily: "Manrope, sans-serif",
                },
              },
            },
          }}
        />
      </div>
    );
  };

  const getWeightageFormattedData = (
    weightage: any,
    setFieldValue: any,
    data: any,
    item: any,
  ) => {
    return (
      <div className="form-group form-group-section-four-column weightage-field-container">
        <TextInputComponent
          type="text"
          fieldName="weightage"
          placeholder="0.00"
          value={item?.weightage ? `${item.weightage}` : ""}
          isLocked={false} // Changed to false to make it focusable
          lockedIcons={
            <i
              className="fa fa-percent"
              style={{ color: "#0000008a", fontWeight: "normal" }}
            />
          }
          onChange={(e: any) => {
            if (e?.target?.value === "" || /^\d+$/.test(e?.target?.value)) {
              setFieldValue(
                "selectedObjectiveData",
                data.map((obj: any) =>
                  obj.objectiveId === item.objectiveId
                    ? { ...obj, weightage: e?.target?.value }
                    : obj,
                ),
              );
            }
          }}
        />
      </div>
    );
  };

  const getActionsFormattedData = (
    item: any,
    setFieldValue: any,
    data: any,
  ) => {
    return (
      <button
        style={{
          background: "transparent",
          border: "none",
          padding: 0,
          cursor: item?.isShowDelete ? "not-allowed" : "pointer",
        }}
        onClick={() => {
          if (data?.length > 1) {
            const updatedData = data.filter(
              (obj: any) => obj.objectiveId !== item.objectiveId,
            );
            setFieldValue("selectedObjectiveData", updatedData);
          } else {
            setIsDeleteWarningOpen(true);
            setSelectedDeleteObjective(item);
          }
        }}
      >
        <img
          alt="Delete icon"
          src={deleteRedIcon}
          className="close-icon"
          style={{ cursor: item?.isShowDelete ? "not-allowed" : "pointer" }}
        />
      </button>
    );
  };

  const formattedData = (data: any, setFieldValue: any) => {
    if (Array.isArray(data) && data.length > 0) {
      return data.map((item: any) => {
        const statusForItem = statusListObjectives.find(
          (status: any) => status.statusName === item?.status?.statusName,
        );

        if (!statusForItem) {
          item.status = { statusId: 3, statusName: "Not Started" };
        }

        return {
          id: item.objectiveId,
          title: item?.objectiveName || "-",
          status: getStatusFormattedData(
            item?.status,
            setFieldValue,
            data,
            item,
          ),
          weightage: getWeightageFormattedData(
            item?.weightage,
            setFieldValue,
            data,
            item,
          ),
          actions: getActionsFormattedData(item, setFieldValue, data),
          data: item,
        };
      });
    }
    return [];
  };

  const validationSchema = Yup.object().shape({
    title: Yup.string().trim().required("Title is required"),
    owner: Yup.array().min(1, "Owner is required"),
    startDate: Yup.date()
      .nullable()
      .transform((value, originalValue) => {
        return originalValue === "" ? null : value;
      })
      .typeError("Please select a valid Start Date")
      .min(new Date(1900, 0, 1), "Start Date must be after 1900")
      .required("Start Date is required")
      .test(
        "start-before-end",
        "Start Date cannot be after End Date",
        function (value, context) {
          const { endDate } = context.parent;
          if (value && endDate) {
            return value <= endDate;
          }
          return true;
        },
      ),
    endDate: Yup.date()
      .nullable()
      .transform((value, originalValue) => {
        return originalValue === "" ? null : value;
      })
      .typeError("Please select a valid End Date")
      .min(new Date(1900, 0, 1), "End Date must be after 1900")
      .required("End Date is required")
      .test(
        "end-after-start",
        "End Date cannot be earlier than Start Date",
        function (value: any, context: any) {
          const startDate = context.parent.startDate;
          if (value && startDate) {
            return value >= startDate;
          }
          return true;
        },
      ),
    weightage: Yup.number()
      .typeError("Weightage must be a number")
      .optional()
      .min(0, "Weightage must be at least 0")
      .max(100, "Weightage cannot exceed 100"),
    selectedObjectiveData: Yup.array().min(
      1,
      "At least one linked objective is required",
    ),
  });

  const handleCancelDelete = () => {
    setIsDeleteWarningOpen(false);
  };

  const handleConfirmDelete = (setFieldValue: any, data: any) => {
    const updatedData = data.filter(
      (obj: any) => obj.objectiveId !== selectedDeleteObjective.objectiveId,
    );
    setFieldValue("selectedObjectiveData", updatedData);
    setIsDeleteWarningOpen(false);
    setSelectedDeleteObjective(null);
  };

  const slotsPaperTitle = (props: any, values: any) => {
    const showNoResults =
      !loadingTitles &&
      hasSearched &&
      titleSuggestions.length === 0 &&
      values.title.trim().length >= 2;

    const showLoading = loadingTitles;

    const showSearchHint = titleSuggestions.length === 0;

    const renderContent = () => {
      if (showLoading) {
        return (
          <div
            style={{
              padding: "8px 16px",
              color: "#666",
              fontSize: "14px",
              textAlign: "center",
            }}
          >
            Searching...
          </div>
        );
      }

      if (showNoResults) {
        return (
          <div
            style={{
              padding: "8px 16px",
              color: "#666",
              fontSize: "14px",
              textAlign: "center",
            }}
          >
            No results found.
          </div>
        );
      }

      if (showSearchHint) {
        return (
          <div
            style={{
              padding: "8px 16px",
              color: "#666",
              fontSize: "14px",
              textAlign: "center",
            }}
          >
            Search
          </div>
        );
      }

      return props.children;
    };

    return (
      <div
        {...props}
        style={{
          backgroundColor: "#fff",
          boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
          borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 8px",
            background: "#fff",
            borderBottom: "0.5px solid #eee",
          }}
        >
          <div className="" style={{ fontSize: "14px" }}>
            <InputLabel style={{ fontSize: "14px" }}>
              Existing Key Results
            </InputLabel>
          </div>
          <button
            style={{
              background: "transparent",
              border: "none",
              padding: 0,
              cursor: "pointer",
            }}
            onClick={(e) => {
              e.stopPropagation();
              setTitleSuggestions([]);
              setHasSearched(false);
              setAutocompleteOpen(false);
            }}
          >
            <img
              alt="Close"
              src={CloseIcon}
              style={{
                cursor: "pointer",
                color: "#555",
                margin: "4px 0",
              }}
            />
          </button>
        </div>
        {renderContent()}
      </div>
    );
  };

  return (
    <Dialog
      open={isOpenAddPopup}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      className={`company-dashboard-add-popup-container company-dashboard-edit-key-results-popup-container`}
      scroll="paper"
      onClose={(event, reason) => {
        if (reason === "backdropClick") {
          return;
        }
        onCancel();
      }}
      sx={{
        "& .MuiDialogActions-root": {
          position: "sticky",
          bottom: 0,
        },
      }}
    >
      <DialogTitle id="alert-dialog-title">
        <div className="header-wrapper">
          <div className="header-wrapper-text">Add Key Results</div>
          <div className="image-wrapper">
            <button
              style={{
                background: "transparent",
                border: "none",
                padding: 0,
                cursor: "pointer",
              }}
              onClick={onCancel}
            >
              <img
                src={CloseIcon}
                className="close-icon"
                style={{ cursor: "pointer" }}
                alt="close"
              />
            </button>
          </div>
        </div>
      </DialogTitle>
      <DialogContent>
        <Formik
          initialValues={getInitialValues()}
          validationSchema={validationSchema}
          validateOnChange={true}
          enableReinitialize={true}
          onSubmit={onSubmitHandler}
        >
          {({
            values,
            setFieldValue,
            touched,
            errors,
            validateField,
            isValid,
            submitCount,
          }) => (
            <Form>
              <OverlayScrollbarsComponent
                className="create-lead-capture-popup-confirmation-description-checkbox-input"
                options={{
                  scrollbars: {
                    theme: "os-theme-dark",
                    autoHide: "scroll",
                    autoHideDelay: 400,
                  },
                }}
              >
                <div className="form-group form-text-input-component">
                  <InputLabel>Title</InputLabel>
                  <Autocomplete
                    freeSolo
                    options={titleSuggestions}
                    getOptionLabel={(option: any) =>
                      typeof option === "string" ? option : option.title || ""
                    }
                    loading={loadingTitles}
                    filterOptions={(x) => x}
                    open={autocompleteOpen}
                    onOpen={() => {
                      // Only open if there's meaningful content to show
                      if (
                        values.title.trim().length >= 2 ||
                        titleSuggestions.length > 0 ||
                        loadingTitles
                      ) {
                        setAutocompleteOpen(true);
                      }
                    }}
                    onClose={(event, reason) => {
                      // Always close on blur (clicking outside) or escape key
                      if (reason === "blur" || reason === "escape") {
                        setAutocompleteOpen(false);
                        return;
                      }

                      // For other reasons, keep open if there are results, loading, or no results after search
                      const shouldStayOpen =
                        titleSuggestions.length > 0 ||
                        loadingTitles ||
                        (hasSearched &&
                          !loadingTitles &&
                          values.title.trim().length >= 2);

                      // Only close if shouldn't stay open (not when showing no results)
                      if (!shouldStayOpen) {
                        setAutocompleteOpen(false);
                      }
                    }}
                    disableClearable
                    value={values.title}
                    inputValue={values.title}
                    onInputChange={(event, newInputValue, reason) => {
                      if (reason === "input") {
                        setFieldValue("title", newInputValue);
                        searchKeyResultTitles(newInputValue);
                        // Open dropdown when typing with enough characters
                        if (newInputValue.trim().length >= 2) {
                          setAutocompleteOpen(true);
                        } else {
                          // Close dropdown when text is cleared or too short
                          setAutocompleteOpen(false);
                          setTitleSuggestions([]);
                          setHasSearched(false);
                        }
                      }
                    }}
                    onChange={(event) => {
                      // Prevent selection - only allow typing
                      event.preventDefault();
                    }}
                    onBlur={() => {
                      // Only clear if we're not showing no results message
                      setTimeout(() => {
                        const shouldKeepOpen =
                          titleSuggestions.length > 0 ||
                          loadingTitles ||
                          (hasSearched &&
                            !loadingTitles &&
                            values.title.trim().length >= 2);

                        if (!shouldKeepOpen) {
                          setTitleSuggestions([]);
                          setHasSearched(false);
                          setAutocompleteOpen(false);
                        }
                      }, 200);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Enter Title"
                        slotProps={{
                          htmlInput: {
                            ...params.inputProps,
                            maxLength: 150,
                          },
                        }}
                      />
                    )}
                    renderOption={(props, option: any) => (
                      <Tooltip title="Selection is turned off for this field.">
                        <li
                          {...props}
                          style={{
                            cursor: "not-allowed",
                            padding: "8px 16px",
                            opacity: 0.7,
                            backgroundColor:
                              values?.title?.trim()?.toLowerCase() ===
                              option.title?.trim()?.toLowerCase()
                                ? "#ddddfa"
                                : "transparent !important",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor =
                              values?.title?.trim()?.toLowerCase() ===
                              option.title?.trim()?.toLowerCase()
                                ? "#ddddfa"
                                : "transparent !important";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor =
                              values?.title?.trim()?.toLowerCase() ===
                              option.title?.trim()?.toLowerCase()
                                ? "#ddddfa"
                                : "transparent !important";
                          }}
                        >
                          <div
                            style={{ fontSize: "14px", pointerEvents: "none" }}
                          >
                            {option.title}
                          </div>
                        </li>
                      </Tooltip>
                    )}
                    noOptionsText={(() => {
                      if (loadingTitles) {
                        return "Searching...";
                      }
                      if (values.title.length < 2) {
                        return "Type at least 2 characters";
                      }
                      return "No results found.";
                    })()}
                    slotProps={{
                      listbox: {
                        style: {
                          maxHeight: "200px",
                        },
                      },
                    }}
                    slots={{ paper: (props) => slotsPaperTitle(props, values) }}
                  />
                  <ErrorMessage
                    name="title"
                    component="div"
                    className="error-message"
                  />
                </div>
                <div className="form-groups-two-column">
                  <div className="form-group form-group-section-two-column">
                    <DatePickerComponent
                      fieldName={"startDate"}
                      label="Start Date"
                      value={values?.startDate}
                      onChange={(e: any) => {
                        setFieldValue("startDate", e);
                      }}
                      minDate={dayjs("1900-01-01")}
                      maxDate={
                        values.endDate ? dayjs(values.endDate) : undefined
                      }
                    />
                  </div>
                  <div className="form-group  form-group-section-two-column">
                    <DatePickerComponent
                      fieldName={"endDate"}
                      label="End Date"
                      value={values?.endDate}
                      onChange={(e: any) => {
                        setFieldValue("endDate", e);
                      }}
                      minDate={
                        values.startDate
                          ? dayjs(values.startDate)
                          : dayjs("1900-01-01")
                      }
                    />
                  </div>
                </div>
                <div className="form-groups-two-column">
                  <div className="form-group  form-group-section-two-column">
                    <SelectInputComponent
                      fieldName="owner"
                      placeholder="Select Owner"
                      label="Owner"
                      optional={false}
                      disabled={false}
                      multiple={true}
                      isOwnerDropdown={true}
                      options={employeesList
                        ?.filter((emp: any) =>
                          emp?.roles?.find(
                            (role: any) => role?.roleName === "ELT",
                          ),
                        )
                        ?.map((emp: any) => {
                          return {
                            value: emp.employeeFullName,
                            label: emp.employeeFullName,
                            image: emp.employeeImage || null,
                            avatar: emp.employeeImage
                              ? null
                              : emp.employeeFullName,
                          };
                        })}
                      value={values.owner}
                      onChange={(selectedValues: any[]) => {
                        const filteredValues = selectedValues.filter(
                          (v) => v != null,
                        );
                        setFieldValue("owner", filteredValues);
                        setFieldValue(
                          "ownerId",
                          filteredValues.map(
                            (name) =>
                              employeesList?.find(
                                (emp: any) =>// NOSONAR
                                emp.employeeFullName === name,
                              )?.employeeId,
                          ),
                        );
                        // Trigger validation if form has been submitted before
                        if (touched.owner) {
                          setTimeout(() => {
                            validateField("owner");
                          }, 0);
                        }
                      }}
                    />
                    <ErrorMessage
                      name={"owner"}
                      component="div"
                      className="error-message"
                    />
                  </div>
                </div>
                <div className="company-dashboard-edit-key-results-object-popup-container">
                  <div className="company-dashboard-edit-key-results-object-popup-container-header">
                    <div>Linked Objectives</div>
                    <div className="company-dashboard-edit-key-results-object-popup-container-header-actions">
                      <button
                        style={{
                          background: "transparent",
                          border: "none",
                          padding: 0,
                          cursor: "pointer",
                        }}
                        type="button"
                        onClick={handleOpenAddObjectivePopup}
                      >
                        <img
                          src={addCirculIcon}
                          alt="key-result"
                          style={{ cursor: "pointer" }}
                        />
                      </button>
                    </div>
                  </div>
                </div>
                <div className="company-dashboard-edit-key-results-object-list-container">
                  <div className="error-message">
                    {typeof errors?.selectedObjectiveData === "string"
                      ? errors?.selectedObjectiveData || ""
                      : null}
                  </div>
                  <MUITable
                    columnNames={headers}
                    rowsData={[
                      ...formattedData(
                        values?.selectedObjectiveData,
                        setFieldValue,
                      ),
                    ]}
                  />
                </div>
                {isOpenAddObjectivePopup ? (
                  <CompanyDashboardAddExistingObjectivesPopup
                    isOpenAddPopup={isOpenAddObjectivePopup}
                    onCancel={handleCloseAddObjectivePopup}
                    setLoading={setLoading}
                    setSelectedObjectiveData={(data: any) => {
                      setFieldValue("selectedObjectiveData", data);
                    }}
                    selectedObjectiveData={values?.selectedObjectiveData}
                  />
                ) : (
                  ""
                )}
                {isDeleteWarningOpen ? (
                  <WarningConfirmationPopup
                    isOpen={isDeleteWarningOpen}
                    onClose={handleCancelDelete}
                    onConfirm={() =>
                      handleConfirmDelete(
                        setFieldValue,
                        values?.selectedObjectiveData,
                      )
                    }
                    title="De-link Objective"
                    description="You’re about to remove the last linked objective. Are you sure you want to delink it?"
                    confirmButtonText="Delink"
                    cancelButtonText="Cancel"
                  />
                ) : (
                  ""
                )}
              </OverlayScrollbarsComponent>
              <DialogActions>
                <div className="action-btn-wrapper">
                  <button
                    onClick={onCancel}
                    type="button"
                    className="cancel-btn"
                  >
                    Cancel
                  </button>
                  {submitCount > 0 && !isValid ? (
                    <Tooltip
                      title="Please resolve all errors before submitting."
                      placement="top"
                    >
                      <span>
                        <button
                          type="submit"
                          autoFocus
                          className="action-btn"
                          disabled
                          style={{
                            opacity: 0.5,
                            cursor: "not-allowed",
                          }}
                        >
                          Add
                        </button>
                      </span>
                    </Tooltip>
                  ) : (
                    <button
                      type="submit"
                      //autoFocus
                      className="action-btn"
                      style={{
                        opacity: 1,
                        cursor: "pointer",
                      }}
                    >
                      Add
                    </button>
                  )}
                </div>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default CompanyDashboardAddKeyResultsPopup;
